import { Component, OnInit, ViewChild } from '@angular/core';
import {
  VueAccountManagementService,
  VueFormError,
  VuePasswordStrengthInputComponent,
  VueServerMessage,
  VueServerMessageType,
  VueTranslationService,
  VueUserSessionService,
  VueValidationRules
} from '@vue/vueframework';
import { getFirstErrorControl } from '../shared/connect.util';
import { AccountMaintenanceMessagesService } from '../shared/services/account-maintenance-messages.service';

@Component({
  selector: 'app-change-password',
  styleUrls: [ './change-password.component.scss' ],
  templateUrl: 'change-password.component.html',
  standalone: false
})
export class ChangePasswordComponent implements OnInit {
  @ViewChild('passwordComponent')
  public passwordComponent: VuePasswordStrengthInputComponent;

  /**
   * Password view mode flag
   */
  public passwordView: boolean;

  /**
   * Password edit mode flag
   */
  public passwordEdit: boolean;

  /**
   * I18n bundle used for translations
   */
  public readonly ACCOUNT_MANAGEMENT_BUNDLE_NAME: string = 'PortalBase';

  /**
   * Number of validation rules to be satisfied
   * for the password to be accepted
   */
  public readonly minValidOptionalRules: string = '3';

  /**
   * Default password message to be displayed in the view mode
   */
  public passwordViewDefaultMessage: string;

  /**
    * form error Object containing the details of an invalid form
  */
  public formError = new VueFormError();

  /**
   * i18n keys used by this component
   */
  public translationKeys = [
    'updatePassword.successMessageUpdatePassword',
    'updatePassword.passwordChangeFailed'
  ];

  /**
   * Required rules for the password strength.
   * We currently use default optional rules.
   */
  public requiredRules: VueValidationRules = {
    rules: [
      {
        class: 'validation-disabled',
        label: '',
        labelKey: 'passwordRule.label.minimumLength',
        labelParams: [ 8 ],
        order: 1,
        regex: new RegExp('^.{8,}$')
      }
    ]
  };

  /**
   * Map used to hold the translations
   */
  public translations: Record<string, string> = {};

  public constructor(
    private accountManagementService: VueAccountManagementService,
    private userSession: VueUserSessionService,
    private vueTranslationService: VueTranslationService,
    private accountMaintenanceMessagesService: AccountMaintenanceMessagesService
  ) { }

  public ngOnInit(): void {
    this.translations = this.vueTranslationService.getTranslations(
      this.ACCOUNT_MANAGEMENT_BUNDLE_NAME,
      this.translationKeys
    );
    this.passwordViewDefaultMessage = this.vueTranslationService.getTranslation(this.ACCOUNT_MANAGEMENT_BUNDLE_NAME,
      'passwordTab.notDisplayPassword');
    this.passwordView = true;
    this.passwordEdit = false;
  }

  /**
     * Toggle to edit mode
     */
  public editPassword(): void {
    this.passwordView = false;
    this.passwordEdit = true;
  }

  /**
     * Toggle to view mode
     */
  public changeToViewMode(): void {
    this.passwordView = true;
    this.passwordEdit = false;
  }

  /**
   * Password form valid
   *
   * @returns True is the form is valid.
   */
  public isPasswordResetFormValid(): boolean {
    return this.passwordComponent.passwordResetForm.valid &&
        this.passwordComponent.passwordInput.isStrongPassword &&
        this.passwordComponent.passwordsMatch;
  }

  /**
   * Save the new password
   */
  public save(): void {
    if (!this.isPasswordResetFormValid()) {
      const errorControl = getFirstErrorControl(this.passwordComponent.passwordResetForm);
      this.formError.invalidFormSubmit = true;
      this.formError.fieldInError = errorControl;
      return;
    }
    const siteId = this.userSession.getOAuthToken().idToken.realmDetail.siteID;
    const username = this.userSession.getOAuthToken().idToken.username;
    this.accountManagementService.updateExpiredPassword(
      username,
      this.passwordComponent.passwordInput.newPassword,
      this.passwordComponent.passwordInput.oldPassword,
      siteId).subscribe(
      {
        error: () => {
          this.handleError();
          this.changeToViewMode();
        },
        next: () => {
          this.accountMaintenanceMessagesService.setMessage(
            new VueServerMessage(
              VueServerMessageType.Success,
              this.translations[ 'updatePassword.successMessageUpdatePassword' ],
              true)
          );
          this.changeToViewMode();
        }
      }
    );
  }

  /**
     * This method is used to handle any errors during the service calls
     */
  private handleError(): void {
    this.accountMaintenanceMessagesService.setMessage(
      new VueServerMessage(
        VueServerMessageType.Error,
        this.translations[ 'updatePassword.passwordChangeFailed' ],
        true)
    );
  }
}
